import React, { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import gsap from 'gsap'
import { createUseStyles } from 'react-jss'
import SplitText from 'gsap/SplitText'
import { useInView } from 'react-intersection-observer'
import composeRefs from '../helpers/composeRefs'
import wrapAll from '../helpers/wrapAll'
import unwrap from '../helpers/unwrap'
import useWebFontLoaded from '../hooks/useWebFontLoaded'
import forEach from 'lodash/forEach'
import cn from 'classnames'

gsap.registerPlugin(SplitText)

function useSplitWordsAnimation ({ linesOnly, ready = true, animateOnMount = true, onComplete }) {
  const [inViewRef, inView] = useInView({ threshold: 0.5, triggerOnce: true })
  const [fontsReady, setFontsReady] = useState(false)
  const fontsReadyRef = useWebFontLoaded(useCallback(() => {
    setFontsReady(true)
  }, []))

  const locals = useRef({}).current

  const animateIn = useCallback(() => {
    if (locals.tl) locals.tl.kill()
    const tl = gsap.timeline({ onComplete })
    locals.tl = tl
    if (linesOnly) {
      tl.to(
        locals.targets,
        { ease: 'expo.out', yPercent: 0, stagger: 0.06, duration: 1.2, opacity: 1, visibility: 'visible' }
      )
    } else {
      forEach(locals.targets, (target, i) => {
        tl.to(
          target.children,
          { ease: 'expo.out', yPercent: 0, stagger: 0.06, duration: 1.2, opacity: 1, visibility: 'visible', delay: i * 0.1 },
          0
        )
      })
    }
  }, [linesOnly, onComplete])

  const animateOut = useCallback(() => {
    if (locals.tl) locals.tl.kill()
    const tl = gsap.timeline({ onComplete })
    locals.tl = tl
    if (linesOnly) {
      tl.to(
        locals.targets,
        { ease: 'expo.out', yPercent: 100, duration: 1.2, opacity: 1, visibility: 'visible' }
      )
      tl.set(locals.targets, { yPercent: 100, visibility: 'hidden', opacity: 0 })
    } else {
      forEach(locals.targets, (target, i) => {
        tl.to(
          target.children,
          { ease: 'expo.out', yPercent: 100, duration: 1.2, opacity: 1, visibility: 'visible' },
          0
        )
      })
      tl.set(locals.split.chars, { yPercent: 100, visibility: 'hidden', opacity: 0 })
    }
  }, [linesOnly, onComplete])

  const mountRef = useCallback((el) => {
    if (el) {
      if (!fontsReady || !ready) {
        return
      }
      locals.split = new SplitText(el, { type: 'chars,lines' })
      gsap.set(locals.split.lines, { overflow: 'hidden' })
      locals.targets = locals.split.lines.map(
        line => wrapAll(line.childNodes, document.createElement('div'))
      )
      if (!linesOnly) {
        gsap.set(locals.split.chars, { yPercent: 100, visibility: 'hidden', opacity: 0 })
      } else {
        gsap.set(locals.targets, { yPercent: 100, visibility: 'hidden', opacity: 0 })
      }
      gsap.set(el, { visibility: 'visible' })
      locals.el = el
    } else if (locals.split) {
      locals.targets.forEach(innerLine => {
        unwrap(innerLine)
      })
      locals.split.revert()
      delete locals.targets
      delete locals.split
      delete locals.el
    }
  }, [locals, fontsReady, ready])

  const inViewAndReady = inView && fontsReady && ready
  useEffect(() => {
    if (!locals.el) return
    if (!inViewAndReady || !animateOnMount) return
    animateIn()
  }, [inViewAndReady, animateOnMount, animateIn])

  return {
    ref: composeRefs(inViewRef, fontsReadyRef, mountRef),
    animateIn,
    animateOut
  }
}

const SplitWordsAnimation = forwardRef(({ className, children, srOnlyContent, ready = true, animateOnMount = true, linesOnly = false, onComplete, screenReaderClone = true }, ref) => {
  const classes = useStyles()
  const child = React.Children.only(children)
  const content = child.props.children
  const { ref: containerRef, animateIn, animateOut } = useSplitWordsAnimation({ linesOnly, ready, animateOnMount, onComplete })

  useImperativeHandle(ref, () => ({
    animateIn,
    animateOut
  }), [animateIn, animateOut])

  return React.cloneElement(child, {}, (
    <>
      {screenReaderClone && <div className='srOnly'>{srOnlyContent || content}</div>}
      <div className={cn(className, classes.splitWords)} ref={containerRef} aria-hidden='true'>
        {content}
      </div>
    </>
  ))
})

const useStyles = createUseStyles({
  splitWords: {
    visibility: 'hidden',
    '& > div': {
      marginBottom: '-0.1em',
      '& > div': {
        paddingBottom: '0.1em'
      }
    }
  },
  srOnly: {
    composes: ['srOnly']
  }
})

export default SplitWordsAnimation
